import * as React from "react";

import {
  ancestorPaths,
  buildPathTree,
  treeGroupId,
} from "@/shared/tree/pathTree";

import {
  buildTreeRows,
  pageTreeRows,
  type TreeGroupRow,
  type TreeRow,
} from "./treeRows";
import {
  useFirstPageWhenPastEnd,
  type InMemoryRows,
  type InMemorySortKey,
} from "./useInMemoryRows";
import type { DataTableState } from "./useTable";

/** The table state a tree is sorted, paged and moved around by. */
export type TreeTableState<TLeaf> = Pick<
  DataTableState<TreeRow<TLeaf>>,
  | "sort"
  | "currentPage"
  | "pageSize"
  | "resetCurrentPage"
  | "goToPage"
  | "focusRow"
  | "scrollToRow"
>;

export interface UseTreeRowsParams<TLeaf> {
  /**
   * The table the rows are drawn in: leaves sort by its sort within their
   * parent, and the rows are cut to its page.
   */
  table: TreeTableState<TLeaf>;
  /**
   * Keep stable (memoized) until they change: a new array rebuilds the tree,
   * and while `openAll` holds it opens every group again.
   */
  leaves: readonly TLeaf[];
  /** Keep stable (module-level or memoized): the rows rebuild when it changes. */
  getPath: (leaf: TLeaf) => readonly string[];
  /** Keep stable, as `getPath`. */
  getId: (leaf: TLeaf) => string;
  sortKeys: Readonly<Record<string, InMemorySortKey<TLeaf>>>;
  /**
   * What groups sort by, from the leaves beneath each, under the columns that
   * show a value on groups too. Under any other column groups stay A to Z.
   */
  groupSortKeys?: Readonly<Record<string, InMemorySortKey<readonly TLeaf[]>>>;
  /**
   * Open every group, as while a search or a filter narrows the leaves, so
   * each match shows under its ancestors. Groups closed in this mode stay
   * closed until it ends or the leaves change, as when the search does; the
   * groups open before it are restored after.
   */
  openAll?: boolean;
}

export interface UseTreeRows<TLeaf> extends InMemoryRows<TreeRow<TLeaf>> {
  /** Whether any row has children; with none, the tree is a flat list. */
  hasGroups: boolean;
  /**
   * Open or close `row`'s group. Closing it from a header a page repeats
   * (`continued`) goes to the page with the group's own row and focuses it,
   * since the page it was on no longer holds anything of the group.
   */
  toggle: (row: TreeGroupRow<TLeaf>) => void;
  /** Open the groups at `paths`. */
  expand: (paths: readonly string[]) => void;
  /**
   * Show the leaf `id`: open the groups above it, go to the page that holds
   * it and scroll it into view. Waits for the leaf to arrive, and gives up
   * once the leaves have arrived without it. While `openAll` holds, the
   * groups restored when it ends stay as they were.
   */
  reveal: (id: string) => void;
}

/**
 * The current page of a tree table's rows, for `DataTable`'s `data` and
 * `tree` props, with which groups are open. A page holds display rows rather
 * than leaves, and one that starts inside a group repeats the groups above
 * its first row at its top (see `pageTreeRows`).
 */
export function useTreeRows<TLeaf>({
  table,
  leaves,
  getPath,
  getId,
  sortKeys,
  groupSortKeys,
  openAll = false,
}: UseTreeRowsParams<TLeaf>): UseTreeRows<TLeaf> {
  const { sort, currentPage, pageSize, goToPage, focusRow, scrollToRow } =
    table;
  const [expanded, setExpanded] = React.useState<ReadonlySet<string>>(
    () => new Set(),
  );
  // What the user closed while `openAll` held. Cleared when it changes, and
  // when the leaves do while it holds: a new search or filter opens every
  // group with a match again.
  const [closed, setClosed] = React.useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [closedUnder, setClosedUnder] = React.useState({ openAll, leaves });
  if (closedUnder.openAll !== openAll || closedUnder.leaves !== leaves) {
    setClosedUnder({ openAll, leaves });
    setClosed((prev) => (prev.size === 0 ? prev : new Set()));
  }

  const sortKey = sort === null ? undefined : sortKeys[sort.columnId];
  const groupSortKey =
    sort === null ? undefined : groupSortKeys?.[sort.columnId];
  // The tree's shape changes with the leaves; its rows, with what is open
  // and how it is sorted.
  const tree = React.useMemo(
    () => buildPathTree({ leaves, getPath, getId }),
    [leaves, getPath, getId],
  );
  const rows = React.useMemo(
    () =>
      buildTreeRows({
        tree,
        isExpanded: openAll
          ? (path) => !closed.has(path)
          : (path) => expanded.has(path),
        sortKey,
        groupSortKey,
        sortDirection: sort?.direction,
      }),
    [tree, openAll, closed, expanded, sortKey, groupSortKey, sort],
  );

  useFirstPageWhenPastEnd(table, rows.length);
  const page = React.useMemo(
    () => pageTreeRows(rows, { currentPage, pageSize }),
    [rows, currentPage, pageSize],
  );

  // Undoes closing the groups at `paths` while `openAll` holds.
  const reopen = React.useCallback((paths: readonly string[]) => {
    setClosed((prev) =>
      paths.some((path) => prev.has(path))
        ? new Set([...prev].filter((path) => !paths.includes(path)))
        : prev,
    );
  }, []);

  const expand = React.useCallback(
    (paths: readonly string[]) => {
      if (paths.length === 0) {
        return;
      }
      setExpanded((prev) =>
        paths.every((path) => prev.has(path))
          ? prev
          : new Set([...prev, ...paths]),
      );
      reopen(paths);
    },
    [reopen],
  );

  // The row to focus once the page that holds it is drawn.
  const [rowToFocus, setRowToFocus] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (
      rowToFocus !== null &&
      page.visibleRows.some((row) => row.id === rowToFocus)
    ) {
      focusRow(rowToFocus);
      setRowToFocus(null);
    }
  }, [rowToFocus, page.visibleRows, focusRow]);

  const toggle = React.useCallback(
    (row: TreeGroupRow<TLeaf>) => {
      const flip = (prev: ReadonlySet<string>) => {
        const next = new Set(prev);
        if (next.has(row.path)) {
          next.delete(row.path);
        } else {
          next.add(row.path);
        }
        return next;
      };
      if (openAll) {
        setClosed(flip);
      } else {
        setExpanded(flip);
      }
      if (row.continued) {
        // Closing the group doesn't move its own row, so its index now gives
        // its page after the close too.
        const ownId = treeGroupId(row.path);
        const index = rows.findIndex((candidate) => candidate.id === ownId);
        goToPage(Math.floor(index / pageSize));
        setRowToFocus(ownId);
      }
    },
    [openAll, rows, pageSize, goToPage],
  );

  // The leaf `reveal` was last asked for, until it is on the page and in view.
  const [leafToReveal, setLeafToReveal] = React.useState<string | null>(null);
  const reveal = React.useCallback((id: string) => setLeafToReveal(id), []);
  React.useEffect(() => {
    if (leafToReveal === null) {
      return;
    }
    const node = tree.getNode(leafToReveal);
    if (node?.kind !== "leaf") {
      if (leaves.length > 0) {
        setLeafToReveal(null);
      }
      return;
    }
    // The groups above the leaf, and its own path: a leaf named like a group
    // is listed inside that group. While `openAll` holds, they are open
    // unless closed in it, and the groups that come back when it ends are
    // left as they were.
    const paths = [...ancestorPaths(node.path), node.path];
    if (openAll) {
      reopen(paths);
    } else {
      expand(paths);
    }
    const index = rows.findIndex((row) => row.id === leafToReveal);
    if (index === -1) {
      return;
    }
    const leafPage = Math.floor(index / pageSize);
    if (leafPage !== currentPage) {
      goToPage(leafPage);
      return;
    }
    scrollToRow(leafToReveal);
    setLeafToReveal(null);
  }, [
    leafToReveal,
    tree,
    leaves,
    openAll,
    reopen,
    expand,
    rows,
    pageSize,
    currentPage,
    goToPage,
    scrollToRow,
  ]);

  return {
    ...page,
    hasGroups: tree.hasGroups,
    toggle,
    expand,
    reveal,
  };
}
